import { useState, useEffect, useCallback } from "react";
import { getDateRangeForKey, getDisplayRangeForKey, matchDatesToRangeKey } from "./dateRange";

const useDateRangeFilter = (defaultRangeKey = "this_week", onFilterChange, storageKey = null) => {

  // ─── 1. Read saved state if storageKey provided, else use default ──────────
  const getInitialState = () => {
    if (storageKey) {
      try {
        const saved = sessionStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          return {
            range: parsed.range || defaultRangeKey,
            start: parsed.start || "",
            end:   parsed.end   || "",
          };
        }
      } catch (e) {
        console.error("useDateRangeFilter: sessionStorage read failed", e);
      }
    }
    const display = getDisplayRangeForKey(defaultRangeKey);
    return { range: defaultRangeKey, start: display.start, end: display.end };
  };

  const initial = getInitialState();

  const [activeRange, setActiveRange] = useState(initial.range);
  const [dateFilterState, setDateFilterState] = useState({
    start: initial.start,
    end:   initial.end,
    showDateRangeDropdown: true,
  });

  // ─── 2. Save to storage whenever filters change ────────────────────────────
  useEffect(() => {
    if (!storageKey) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({
        range: activeRange,
        start: dateFilterState.start,
        end:   dateFilterState.end,
      }));
    } catch (e) {
      console.error("useDateRangeFilter: sessionStorage write failed", e);
    }
  }, [storageKey, activeRange, dateFilterState.start, dateFilterState.end]);

  // ─── Notify parent (debounced) — unchanged ────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!onFilterChange) return;
      if (activeRange === "custom") {
        if (dateFilterState.start && dateFilterState.end) {
          onFilterChange({ startDate: dateFilterState.start, endDate: dateFilterState.end, rangeKey: "custom" });
        }
      } else if (activeRange === "all") {
        onFilterChange({ startDate: null, endDate: null, rangeKey: "all" });
      } else {
        const range = getDateRangeForKey(activeRange);
        onFilterChange({ startDate: range.start, endDate: range.end, rangeKey: activeRange });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [activeRange, dateFilterState.start, dateFilterState.end]);

  // ─── Handlers — unchanged ─────────────────────────────────────────────────
  const handleRangeChange = useCallback((key) => {
    setActiveRange(key);
    if (key === "all") {
      setDateFilterState({ start: "", end: "", showDateRangeDropdown: true });
    } else {
      const display = getDisplayRangeForKey(key);
      setDateFilterState({ start: display.start, end: display.end, showDateRangeDropdown: true });
    }
  }, []);

  const handleDateChange = useCallback((type, value) => {
    setDateFilterState((prev) => {
      const next = { ...prev, [type === "start" ? "start" : "end"]: value };
      const matched = matchDatesToRangeKey(next.start, next.end);
      next.showDateRangeDropdown = matched !== null;
      if (matched) setActiveRange(matched);
      else setActiveRange("custom");
      return next;
    });
  }, []);

  // ─── 3. Clear also removes from storage ───────────────────────────────────
  const handleClearDateFilter = useCallback(() => {
    if (storageKey) sessionStorage.removeItem(storageKey);
    const display = getDisplayRangeForKey(defaultRangeKey);
    setActiveRange(defaultRangeKey);
    setDateFilterState({ start: display.start, end: display.end, showDateRangeDropdown: true });
  }, [defaultRangeKey, storageKey]);

  return { dateFilterState, activeRange, handleDateChange, handleRangeChange, handleClearDateFilter };
};

export default useDateRangeFilter;